/**
 * Pruefwarteschlange der Produktmerkmale.
 *
 * WOFUER SIE DA IST
 *
 * 994 maschinell gewonnene Merkmalswerte liegen an 253 Artikeln. Ueber die
 * Artikelansicht geprueft hiesse: suchen, oeffnen, lesen, schliessen -- 253
 * mal. Diese Ansicht dreht das um und stellt die WERTE in den Mittelpunkt.
 *
 * DIE DREI ENTSCHEIDUNGEN, DIE DIE ANSICHT PRAEGEN
 *
 *   1. Alles Noetige steht in der Zeile. Wert, Einheit, Status,
 *      Entstehungsweise, Quellwortlaut und Befund -- ohne Aufklappen. Ein
 *      Wert, dessen Herkunft man erst suchen muss, wird nicht geprueft,
 *      sondern durchgewinkt.
 *
 *   2. Es gibt KEIN "Alle bestaetigen". Jede Bestaetigung ist ein eigener
 *      Aufruf mit eigener Rechtepruefung und eigenem Protokolleintrag. Eine
 *      Schaltflaeche, die 199 Werte auf einmal belastbar setzt, waere genau
 *      die Massenbestaetigung, die dieser Bestand nicht braucht.
 *
 *   3. Geaendert wird hier nicht. Wer einen Wert korrigieren will, geht in
 *      den Artikel -- dort steht das vorhandene Formular. Ein zweites
 *      Eingabeformular waere ein zweiter Ort, an dem eine Regel fehlen kann.
 *
 * Der Befund ist NICHT der Qualitaetsstatus. Er sagt, was die Auswertung
 * gesehen hat; ob ein Wert fachlich gilt, sagt allein der Status -- und den
 * setzt ein Mensch.
 */
import { useCallback, useEffect, useState } from "react";
import type {
  MetaResponse, PropertyFinding, PropertyReviewItem, PropertyReviewResponse,
} from "@homebase/shared";
import { api } from "../lib/api.ts";
import { useI18n, type I18n } from "../i18n/index.tsx";
import { errorText } from "../i18n/errors.ts";
import { EmptyState, ErrorState, LoadingState } from "./States.tsx";
import { formatNumber } from "./ProductProperties.tsx";
import "./PropertyReview.css";

const KLASSEN = ["A", "B", "C", "D", "E", "F"] as const;

export function PropertyReview({ meta, mayWrite, mayConfirm, onOpenArticle }: {
  meta: MetaResponse;
  mayWrite: boolean;
  mayConfirm: boolean;
  onOpenArticle: (articleId: number) => void;
}): React.JSX.Element {
  const i18n = useI18n();
  const t = i18n.t;

  const [findingClass, setFindingClass] = useState<string>("");
  const [definition, setDefinition] = useState<string>("");
  const [quality, setQuality] = useState<string>("");
  const [role, setRole] = useState<string>("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const [data, setData] = useState<PropertyReviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  /** Welche Zeile wird gerade geschrieben? Sperrt genau diese, nicht die Liste. */
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await api.propertyReview({
        q: search || undefined,
        findingClass: findingClass || undefined,
        definition: definition || undefined,
        quality: (quality || undefined) as never,
        role: role || undefined,
        page,
      }));
    } catch (e) { setError(e); } finally { setLoading(false); }
  }, [search, findingClass, definition, quality, role, page]);

  useEffect(() => { void load(); }, [load]);

  /**
   * Status setzen -- genau ein Wert, genau ein Aufruf.
   *
   * Der Weg ist derselbe wie in der Artikelansicht: `PATCH /properties/{id}`.
   * Damit greift dieselbe Pruefung auf `property.confirm`, und es entsteht
   * derselbe Protokolleintrag. Diese Ansicht kennt keinen kuerzeren Weg.
   */
  async function setzeStatus(id: number, wert: "belastbar" | "pruefung_erforderlich"): Promise<void> {
    setBusyId(id);
    setError(null);
    try {
      await api.updateProperty(id, { quality: wert });
      // Nur die betroffene Zeile fortschreiben. Ein vollstaendiges Neuladen
      // riss dem Pruefer die Liste unter dem Blick weg.
      setData((d) => d === null ? d : {
        ...d,
        items: d.items.map((i) => i.value.id === id
          ? { ...i, value: { ...i.value, quality: wert } } : i),
        counts: { ...d.counts, byQuality: zaehleUm(d.counts.byQuality, id, d, wert) },
      });
    } catch (e) { setError(e); } finally { setBusyId(null); }
  }

  const zurueck = (): void => {
    setFindingClass(""); setDefinition(""); setQuality(""); setRole(""); setSearch(""); setPage(1);
  };
  const gefiltert = Boolean(findingClass || definition || quality || role || search);
  const seiten = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  return (
    <div className="review">
      <div className="review__head">
        <h2 className="review__title">{t("review.title")}</h2>
        <p className="review__lead">{t("review.lead")}</p>
      </div>

      {data?.counts && <Fortschritt counts={data.counts} i18n={i18n} />}

      {/* Herkunft der Befunde. Ein Befund gegen einen ueberholten Quellstand
          darf nicht wie ein aktueller aussehen. */}
      {data?.run && (
        <p className={data.run.sourceChanged ? "review__run review__run--stale" : "review__run"}>
          {t("review.runInfo", {
            checksum: data.run.sourceChecksum,
            second: data.run.secondChecksum ?? t("review.noSecondSource"),
          })}
          {data.run.sourceChanged && <> — <strong>{t("review.runStale")}</strong></>}
        </p>
      )}
      {data && data.run === null && <p className="review__run">{t("review.noRun")}</p>}

      <div className="review__toolbar">
        <input className="list__search" value={search} aria-label={t("review.search")}
               placeholder={t("review.searchPlaceholder")}
               onChange={(e) => { setSearch(e.target.value); setPage(1); }} />

        <select className="list__select" value={findingClass} aria-label={t("review.findingClass")}
                onChange={(e) => { setFindingClass(e.target.value); setPage(1); }}>
          <option value="">{t("review.allClasses")}</option>
          {KLASSEN.map((k) => (
            <option key={k} value={k}>
              {k} — {i18n.tCode("reviewClass", k, k)}
              {data?.counts.byClass[k] ? ` (${data.counts.byClass[k]})` : ""}
            </option>
          ))}
        </select>

        <select className="list__select" value={definition} aria-label={t("review.property")}
                onChange={(e) => { setDefinition(e.target.value); setPage(1); }}>
          <option value="">{t("review.allProperties")}</option>
          {meta.propertyDefinitions.map((d) => (
            <option key={d.code} value={d.code}>{i18n.tCode("prop", d.code, d.code)}</option>
          ))}
        </select>

        <select className="list__select" value={quality} aria-label={t("review.quality")}
                onChange={(e) => { setQuality(e.target.value); setPage(1); }}>
          <option value="">{t("review.allQualities")}</option>
          {["pruefung_erforderlich", "warnung", "belastbar"].map((q) => (
            <option key={q} value={q}>{i18n.tCode("quality", q, q)}</option>
          ))}
        </select>

        <select className="list__select" value={role} aria-label={t("review.role")}
                onChange={(e) => { setRole(e.target.value); setPage(1); }}>
          <option value="">{t("review.allRoles")}</option>
          {meta.roles.map((r) => (
            <option key={r.id} value={r.code}>{i18n.tCode("articleRole", r.code, r.name)}</option>
          ))}
        </select>

        {gefiltert && <button className="btn list__reset" onClick={zurueck}>{t("list.resetFilters")}</button>}
      </div>

      <div className="list__meta">
        {data && !loading && (
          <span><strong>{data.total}</strong> {t("review.values")}
            {gefiltert && <> {t("list.ofTotal", { total: data.counts.total })}</>}</span>
        )}
      </div>

      {error !== null && <p className="review__error" role="alert">{errorText(i18n, error)}</p>}
      {loading && !data && <LoadingState label={t("review.loading")} />}
      {error !== null && !data && <ErrorState message={errorText(i18n, error)} onRetry={load} />}

      {data && (data.items.length === 0
        ? <EmptyState title={t("review.emptyTitle")} hint={t("review.emptyHint")} />
        : (
          <>
            <div className={loading ? "review__list review__list--busy" : "review__list"}>
              {data.items.map((item) => (
                <Zeile key={item.value.id} item={item} i18n={i18n}
                       mayWrite={mayWrite} mayConfirm={mayConfirm}
                       busy={busyId === item.value.id}
                       onConfirm={() => void setzeStatus(item.value.id, "belastbar")}
                       onDefer={() => void setzeStatus(item.value.id, "pruefung_erforderlich")}
                       onOpenArticle={() => onOpenArticle(item.articleId)} />
              ))}
            </div>
            {seiten > 1 && (
              <div className="list__pager">
                <button className="btn" disabled={page <= 1} onClick={() => setPage(page - 1)}>{t("list.prev")}</button>
                <span>{t("list.page", { page, total: seiten })}</span>
                <button className="btn" disabled={page >= seiten} onClick={() => setPage(page + 1)}>{t("list.next")}</button>
              </div>
            )}
          </>
        ))}
    </div>
  );
}

/** Zaehlwerk nach einer Statusaenderung fortschreiben, ohne neu zu laden. */
function zaehleUm(
  byQuality: Readonly<Record<string, number>>, id: number,
  d: PropertyReviewResponse, neu: string,
): Record<string, number> {
  const alt = d.items.find((i) => i.value.id === id)?.value.quality;
  if (!alt || alt === neu) return { ...byQuality };
  return { ...byQuality, [alt]: (byQuality[alt] ?? 1) - 1, [neu]: (byQuality[neu] ?? 0) + 1 };
}

/**
 * Der Fortschritt ueber den GESAMTEN Bestand.
 *
 * Bewusst nicht ueber die angezeigte Seite: eine Fortschrittsanzeige, die nur
 * die aktuelle Seite kennt, ist eine Fortschrittstaeuschung.
 */
function Fortschritt({ counts, i18n }: {
  counts: PropertyReviewResponse["counts"]; i18n: I18n;
}): React.JSX.Element {
  const t = i18n.t;
  const belastbar = counts.byQuality["belastbar"] ?? 0;
  const anteil = counts.total === 0 ? 0 : Math.round((belastbar / counts.total) * 100);
  return (
    <div className="review__progress">
      <div className="review__bar" role="img"
           aria-label={t("review.progressAria", { done: belastbar, total: counts.total })}>
        <div className="review__barfill" style={{ width: `${anteil}%` }} />
      </div>
      <div className="review__counts">
        <span><strong>{belastbar}</strong> / {counts.total} {i18n.tCode("quality", "belastbar", "belastbar")}</span>
        <span>{counts.byQuality["warnung"] ?? 0} {i18n.tCode("quality", "warnung", "warnung")}</span>
        <span>{counts.byQuality["pruefung_erforderlich"] ?? 0} {i18n.tCode("quality", "pruefung_erforderlich", "pruefung_erforderlich")}</span>
      </div>
    </div>
  );
}

/**
 * Anzeige eines strukturierten Wertes samt Einheit.
 *
 * Dieselben Regeln wie im Artikeldetail -- Schranke als Zeichen, Zahl in der
 * Schreibweise der Anzeigesprache. Zwei Schreibweisen fuer denselben Wert
 * waeren zwei Aussagen; wer hier prueft, muss genau das sehen, was der
 * Artikel zeigt.
 */
function wertText(item: PropertyReviewItem, i18n: I18n): string {
  const v = item.value;
  const einheit = item.unit ? ` ${item.unit}` : "";
  const z = (raw: string): string => formatNumber(raw, i18n.lang);
  switch (item.valueType) {
    case "boolean": return i18n.t(v.bool ? "props.yes" : "props.no");
    case "enum":    return i18n.tCode("propValue", v.code ?? "", v.code ?? "");
    case "text":    return v.text ?? "";
    case "range": {
      if (v.min !== null && v.max !== null) {
        return v.min === v.max ? `${z(v.min)}${einheit}` : `${z(v.min)}–${z(v.max)}${einheit}`;
      }
      if (v.max !== null) return `≤ ${z(v.max)}${einheit}`;
      return `≥ ${z(v.min!)}${einheit}`;
    }
    default: return v.num === null ? "" : `${z(v.num)}${einheit}`;
  }
}

function Zeile({ item, i18n, mayWrite, mayConfirm, busy, onConfirm, onDefer, onOpenArticle }: {
  item: PropertyReviewItem; i18n: I18n;
  mayWrite: boolean; mayConfirm: boolean; busy: boolean;
  onConfirm: () => void; onDefer: () => void; onOpenArticle: () => void;
}): React.JSX.Element {
  const t = i18n.t;
  const v = item.value;
  const bestaetigt = v.quality === "belastbar";

  return (
    <article className={`review__row review__row--${v.quality}`}>
      <div className="review__main">
        <button className="review__article" onClick={onOpenArticle}
                title={t("review.openArticle")}>{item.articleNumber}</button>
        <span className="review__prop">{i18n.tCode("prop", item.definition, item.definition)}</span>
        <span className="review__value">{wertText(item, i18n)}</span>
        {/* Diese Ansicht ist die Pruefwarteschlange selbst -- wer sie
            aufrufen kann, hat `property.review.read`, und dann liefert der
            Server Methode und Pruefstand. Die Ausweichwerte sind unerreichbar
            und stehen nur da, weil der Typ die Redaktion zulaesst. */}
        <span className="review__method">
          {i18n.tCode("propMethod", v.method ?? "", v.method ?? "")}</span>
        <span className={`props__q props__q--${v.quality}`}>
          {i18n.tCode("quality", v.quality ?? "", v.quality ?? "")}</span>
      </div>

      {/*
        Der Quellwortlaut steht in der Zeile, nicht hinter einem Aufklapper.
        Er ist der eigentliche Pruefgegenstand: ohne ihn ist die Zahl daneben
        nur eine Behauptung.
      */}
      {v.sourceSegment && (
        <p className="review__source">
          <span className="review__label">{t("props.sourceSegment")}:</span> «{v.sourceSegment}»
        </p>
      )}

      {item.findings.map((f) => <BefundZeile key={f.code} f={f} i18n={i18n} />)}

      <div className="review__actions">
        {/* Bestaetigen verlangt property.confirm. Fehlt das Recht, erscheint
            die Schaltflaeche nicht -- abgewiesen wuerde es ohnehin im Server. */}
        {mayConfirm && !bestaetigt && (
          <button className="btn btn--primary" disabled={busy} onClick={onConfirm}>
            {t("review.confirm")}
          </button>
        )}
        {mayWrite && bestaetigt && (
          <button className="btn" disabled={busy} onClick={onDefer}>{t("review.defer")}</button>
        )}
        {mayWrite && !bestaetigt && v.quality !== "pruefung_erforderlich" && (
          <button className="btn" disabled={busy} onClick={onDefer}>{t("review.defer")}</button>
        )}
        {/* Geaendert wird im Artikel -- dort steht das vorhandene Formular. */}
        <button className="btn" onClick={onOpenArticle}>{t("review.edit")}</button>
      </div>
    </article>
  );
}

function BefundZeile({ f, i18n }: { f: PropertyFinding; i18n: I18n }): React.JSX.Element {
  return (
    <p className={`review__finding review__finding--${f.findingClass}`}>
      <span className="review__badge" title={i18n.tCode("reviewClass", f.findingClass, f.findingClass)}>
        {f.findingClass}
      </span>
      <span className="review__findingcode">{i18n.tCode("finding", f.code, f.code)}</span>
      {f.detail && <span className="review__detail">{f.detail}</span>}
    </p>
  );
}
